import moment from 'moment';
import { AlertCriticalIcon } from '@fortress-technology-solutions/fortress-component-library/Icons';
import {
  VALID_ID,
  VALID_ID_AFFORDABLE,
} from '../../../components/EditOccupantModal/constants';
import messages from './messages';

// utils/redux-form-helper's sanitizeNumberInput caps at 7 integer digits —
// built for a different field (ScreeningModal), well under this field's real
// ceiling (resident.schema.js allows up to $999,999,999,999.99), so a
// dedicated strip-formatting-only parse is used here instead.
const parseCurrencyValue = (value) =>
  Number(String(value).replace(/[^0-9.]/g, ''));

// VALID_ID/VALID_ID_AFFORDABLE (EditOccupantModal/constants.js) mention
// Driver's License/State ID and Passport, which don't apply to minors —
// only SSN and Alien Registration # apply to them.
export const VALID_ID_MINOR = 'Must have a valid SSN or Alien Registration';

// Uses the component library's own icon rather than @mui/icons-material,
// whose ESM build doesn't expose a default export in this Vite setup.
const ConditionallyRequiredIcon = () => (
  <AlertCriticalIcon
    color="warning"
    fontSize="small"
    sx={{ ml: 0.5, verticalAlign: 'middle' }}
  />
);

// Every conditionally-required field shows this icon statically (always),
// except the license/SSN/passport/alien-registration group, which toggles
// dynamically — see getIdentificationIconState below.
export const withConditionallyRequiredIcon = (label) => (
  <>
    {label}
    <ConditionallyRequiredIcon />
  </>
);

// A field holding nothing but whitespace renders as blank, so it can't count
// as an identification either.
const hasIdentificationValue = (value) =>
  typeof value === 'string'
    ? value.trim() !== ''
    : !(value === null || value === undefined);

// The SSN is measured by shape rather than emptiness, because a non-empty
// value here is not necessarily an SSN: getResidentHousehold
// (Residents.js#getResidentHousehold) returns Customer.socialSecurityNumber
// still encrypted, and the masked field drops non-digits, so the user sees a
// blank SSN while the form holds base64 ciphertext. Legacy records can also
// hold a literal "XXX-XX-XXXX" or a partial number. Counting any of those left
// Save enabled with no usable identification on file (ENG-31188). The masked
// field hands over digits only; dashes/spaces appear solely in stored values.
const unmaskSsn = (value) => String(value ?? '').replace(/[-\s]/g, '');
// Being typed in — enough to keep the group's icon while the user works on it.
const hasSsnInput = (value) => /^\d{1,9}$/.test(unmaskSsn(value));
// Complete — the only thing that satisfies the Save gate.
const hasCompleteSsn = (value) => /^\d{9}$/.test(unmaskSsn(value));

const FIELD_HAS_INPUT = { socialSecurityNumber: hasSsnInput };
const FIELD_IS_COMPLETE = { socialSecurityNumber: hasCompleteSsn };

// Which fields count together as satisfying "at least one valid ID" — a
// state ID needs both its number and its state to count, same for a
// passport's number+country; SSN/Alien Registration each stand alone.
// Mirrors EditOccupantModal/utils.js's isIdentificationNumberMissing
// pairing, just broken out per-group instead of collapsed to one boolean.
const ADULT_IDENTIFICATION_GROUPS = [
  ['stateIdNumber', 'stateIdOrigin'],
  ['socialSecurityNumber'],
  ['passportNumber', 'passportCountry'],
  ['alienRegistration'],
];
const MINOR_IDENTIFICATION_GROUPS = [
  ['socialSecurityNumber'],
  ['alienRegistrationNumber'],
];

// Each group keeps its icon/helper text once the user has started filling
// it in (either field of a pair), and loses it once some OTHER group has
// input instead — while every field still shows it in the original, fully-
// blank state. This lets the icon mark "which path you're completing"
// rather than disappearing everywhere the moment any one field is touched.
export const getIdentificationIconState = ({
  personalInformation,
  isAdult,
  isAffordable,
}) => {
  const groups = isAdult
    ? ADULT_IDENTIFICATION_GROUPS
    : MINOR_IDENTIFICATION_GROUPS;
  const groupHasInput = (group) =>
    group.some((field) =>
      (FIELD_HAS_INPUT[field] ?? hasIdentificationValue)(
        personalInformation[field],
      ),
    );
  const groupIsComplete = (group) =>
    group.every((field) =>
      (FIELD_IS_COMPLETE[field] ?? hasIdentificationValue)(
        personalInformation[field],
      ),
    );
  const anyGroupHasInput = groups.some(groupHasInput);

  const fieldsShowingIcon = groups.reduce((fields, group) => {
    const showIcon = !anyGroupHasInput || groupHasInput(group);
    group.forEach((field) => {
      fields[field] = showIcon;
    });
    return fields;
  }, {});

  const identificationHelperText = isAdult
    ? isAffordable
      ? VALID_ID_AFFORDABLE
      : VALID_ID
    : VALID_ID_MINOR;

  return {
    fieldsShowingIcon,
    identificationHelperText,
    // Unlike every other conditionally-required field, this group actually
    // blocks Save once none of the four is a complete, usable ID — reusing
    // the same helper text already shown on every field in that state as
    // the explanation, rather than a separate message. A pair (State ID #/
    // State, Passport #/Country) only counts once BOTH halves are filled
    // in; half a pair isn't an identification on its own.
    hasAtLeastOneIdentification: groups.some(groupIsComplete),
  };
};

// The component library's socialSecurity field unmasks (strips non-digits)
// before validating, so "123-45-6789" is a complete 9-digit SSN — mirror
// that here rather than testing the raw dash-formatted string directly.
const countSsnDigits = (ssn) => (ssn ?? '').replace(/\D/g, '').length;

// The masked SSN field pads unfilled digits with a literal "X" inside the
// string itself (e.g. "343-43-XXXX"), so it's always the same length
// whether it's fully typed or not — a generic minLength/maxLength rule
// checking the raw string's length can never tell those apart. Only an
// actual digit count does. Legacy records can also have an incomplete/
// malformed SSN already on file; that untouched value is let through as-is
// (comparing against the exact original, not just "some" length) so a
// pre-existing bad record doesn't permanently block Save over a field the
// user never touched — but typing anything else still needs real 9 digits.
export const getSsnFieldRules = ({ ssn, formatMessage }) => ({
  validate: (value) => {
    if (!value || value === ssn) return true;
    if (countSsnDigits(value) !== 9) {
      return formatMessage(messages.ssnInvalid);
    }
    return true;
  },
});

// A license/passport number and its State/Country are saved together or not
// at all: either half filled while the other is blank is an error on the blank
// one. Both halves blank is fine. Select blanks arrive as '' or 'default'.
const isPairHalfBlank = (value) =>
  !hasIdentificationValue(value) || value === 'default';

export const getIdentificationPairRules = ({ partnerName, message }) => ({
  validate: (value, getValues) =>
    !isPairHalfBlank(value) ||
    isPairHalfBlank(getValues(partnerName)) ||
    message,
});

// The email field variant's own hard-coded default validation pattern
// (traced into the compiled bundle) — mirrored here so we can tell whether
// a loaded value already fails it, same reasoning as countSsnDigits above.
export const EMAIL_PATTERN =
  /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9]+([.-][a-zA-Z0-9]+)*\.[a-zA-Z]{2,}$/;

// Email is required for adults unconditionally, but a Pending Applicant
// (or a member with an incomplete legacy record) can have no email at
// all — required-but-empty permanently blocked Save. The email variant
// also hard-codes a pattern stricter than some real addresses (no
// apostrophes, ASCII-only domains). Relaxed only when the loaded value is
// already missing or already fails the pattern; a fresh edit is still
// held to the real format and still required.
export const getEmailFieldRules = ({ isAdult, email, isAdding }) => {
  if (!isAdult) return undefined;
  // A brand-new member has no pre-existing gap to protect, so the relaxation
  // below must not apply to it — the field list marks Email as Required for
  // adults, i.e. it blocks the save.
  if (isAdding) return { required: true };
  const hasPreexistingIssue = !email || !EMAIL_PATTERN.test(email);
  return hasPreexistingIssue ? { pattern: '.*' } : { required: true };
};

// Same "don't block Save over a pre-existing gap" reasoning as email
// above — Phone is also unconditionally required for adults.
export const getPhoneFieldRules = ({ isAdult, phoneNumber, isAdding }) => {
  if (!isAdult) return undefined;
  return isAdding || phoneNumber ? { required: true } : undefined;
};

// Same reasoning again — Type is required unconditionally, but a member
// record could already be missing one. Not disabled-and-required like
// Start Date was (Current/Prior can still edit these), but a fresh, blank
// required field is exactly as blocking.
export const getApplicantTypeFieldRules = (applicantTypeId, isAdding) =>
  isAdding || applicantTypeId ? { required: true } : undefined;

// 'default' is App/selectors.js getRelationshipOptions' blank "Choose" value,
// not a real relationship id -- parseGeneralInfo hands it to every adult and
// minor with none on file (useHouseholdMembersQuery.helpers.js), and
// buildSavePayload turns it straight back into null. react-hook-form's
// `required` sees only a non-empty string, so it has to be spelled out as
// blank in both halves below: in the relaxation, or a member who never had a
// Relation would be failed from first render; and in the validate, or a
// Relation blanked back out saves as null while nominally required.
const isBlankRelationship = (relationshipId) =>
  !relationshipId || relationshipId === 'default';

// Relationship is required unless the member is primary (head of
// household) — same pre-existing-gap relaxation as Type above, except for
// minors: the application form never captures their Relation, so they always
// open blank and the relaxation would let them save without one.
export const getRelationshipFieldRules = ({
  isPrimary,
  isMinor = false,
  relationshipId,
  isAdding,
  formatMessage,
}) => {
  if (isPrimary) return undefined;
  if (!isMinor && !isAdding && isBlankRelationship(relationshipId)) {
    return undefined;
  }
  return {
    required: true,
    validate: (value) =>
      isBlankRelationship(value)
        ? // RHF wants `true | string`: falling back to `false` would flag the
          // field invalid with no message to render, and to `undefined` would
          // make it pass outright.
          formatMessage?.(messages.relationRequired) ??
          messages.relationRequired.defaultMessage
        : true,
  };
};

// Same 'default'-is-blank problem as Relation above: react-hook-form's
// `required` counts the "Choose" placeholder as filled, so picking a real
// option and then going back to "Choose" left Save enabled. The edit path was
// already covered by wasAffordableRelationshipCleared (which needs a saved
// value to compare against); this is what covers Add, where there is none.
export const getAffordableRelationshipRules = ({ formatMessage }) => ({
  required: true,
  validate: (value) =>
    isBlankRelationship(value)
      ? formatMessage?.(messages.affordableRelationRequired) ??
        messages.affordableRelationRequired.defaultMessage
      : true,
});

// Start/End Date cross-field + household-date validation. Skipped entirely
// while the field is disabled (Pending or an open certification already
// blocks editing) — otherwise a pre-existing bad legacy date on a field the
// user can't even touch would permanently block Save, the same failure
// mode as the SSN/Email/Phone relaxations above.
export const getStartDateValidate =
  ({
    disabled,
    householdMoveInDate,
    isPriorResident,
    householdMoveOutDate,
    formatMessage,
  }) =>
  (value, getValues) => {
    if (disabled || !value) return true;
    const start = moment(value);
    if (!start.isValid()) return true;
    if (
      householdMoveInDate &&
      start.isBefore(moment(householdMoveInDate), 'day')
    ) {
      return formatMessage(messages.startDateBeforeMoveIn);
    }
    const endValue = getValues('generalInformation.endDate');
    if (endValue && start.isAfter(moment(endValue), 'day')) {
      return formatMessage(messages.startDateAfterEndDate);
    }
    if (start.isAfter(moment(), 'day')) {
      return formatMessage(messages.startDateInFuture);
    }
    if (
      isPriorResident &&
      householdMoveOutDate &&
      start.isAfter(moment(householdMoveOutDate), 'day')
    ) {
      return formatMessage(messages.startDateAfterMoveOut);
    }
    return true;
  };

// A household must always have exactly one Head of Household, so the
// current HoH can't be given an End Date here — removing/aging them out
// requires assigning a new HoH first. Checked ahead of the other rules
// since none of the date-range reasons below apply to this case.
//
// The HoH branch fires on a CHANGE only, comparing against the loaded value
// exactly as getSsnFieldRules/getEmailFieldRules above do. The HoH's End Date
// field is disabled, so a legacy value already on the record can never be
// cleared from here -- blocking on it would lock the user out of saving the
// fields that ARE still editable, over an error they have no way to fix.
// Giving a HoH a new End Date is still rejected.
export const getEndDateValidate =
  ({
    disabled,
    householdMoveInDate,
    isPriorResident,
    householdMoveOutDate,
    isPrimary,
    endDate,
    formatMessage,
  }) =>
  (value, getValues) => {
    if (disabled || !value) return true;
    if (isPrimary) {
      if (value === endDate) return true;
      return formatMessage(messages.headOfHouseholdEndDateHelperText);
    }
    const end = moment(value);
    if (!end.isValid()) return true;
    const startValue = getValues('generalInformation.startDate');
    if (startValue && end.isBefore(moment(startValue), 'day')) {
      return formatMessage(messages.endDateBeforeStartDate);
    }
    if (
      householdMoveInDate &&
      end.isBefore(moment(householdMoveInDate), 'day')
    ) {
      return formatMessage(messages.endDateBeforeMoveIn);
    }
    if (end.isAfter(moment(), 'day')) {
      return formatMessage(messages.endDateInFuture);
    }
    if (
      isPriorResident &&
      householdMoveOutDate &&
      end.isAfter(moment(householdMoveOutDate), 'day')
    ) {
      return formatMessage(messages.endDateAfterMoveOut);
    }
    return true;
  };

// A minor can be an unborn child, entered with an expected Date of Birth.
export const MAX_MONTHS_AHEAD_MINOR_DATE_OF_BIRTH = 10;

export const getLatestMinorDateOfBirth = () =>
  moment().add(MAX_MONTHS_AHEAD_MINOR_DATE_OF_BIRTH, 'months');

// Blocks a manually-typed future Date of Birth — disableFuture on the date
// picker (useMiniProfileSections.js) only stops picking one from the
// calendar, not typing one directly into the field.
export const getDateOfBirthValidate =
  ({ formatMessage }) =>
  (value) => {
    if (!value) return true;
    const dateOfBirth = moment(value);
    if (!dateOfBirth.isValid()) return true;
    if (dateOfBirth.isAfter(moment(), 'day')) {
      return formatMessage(messages.dateOfBirthInFuture);
    }
    return true;
  };

// Same guard for a minor, with maxDate on its picker in place of disableFuture.
export const getMinorDateOfBirthValidate =
  ({ formatMessage }) =>
  (value) => {
    if (!value) return true;
    const dateOfBirth = moment(value);
    if (!dateOfBirth.isValid()) return true;
    if (dateOfBirth.isAfter(getLatestMinorDateOfBirth(), 'day')) {
      return formatMessage(messages.minorDateOfBirthTooFarAhead, {
        months: MAX_MONTHS_AHEAD_MINOR_DATE_OF_BIRTH,
      });
    }
    return true;
  };

// Conditionally required (Excel field list), not hard-required — an
// unanswered Annual Income must not block Save, same reasoning as the other
// conditionally-required fields. The currency variant's raw value can be a
// comma/dollar-sign-formatted string, so it's parsed to a plain number first.
export const getAnnualIncomeValidate =
  ({ formatMessage }) =>
  (value) => {
    if (value === '' || value === null || value === undefined) return true;
    const num = parseCurrencyValue(value);
    if (!Number.isFinite(num)) {
      return formatMessage(messages.annualIncomeInvalid);
    }
    if (num > 999999999999.99) {
      return formatMessage(messages.annualIncomeMaxError);
    }
    return true;
  };

// null leaves the four sub-answers alone. Disabling a field does not clear it,
// so an opt-out that skipped this would save details already given -- nothing
// downstream sanitizes them. '' is what buildSavePayload sends as null.
export const resolveDisabilitySubFieldReset = (hasDisability) => {
  if (hasDisability === 'no') return 'no';
  if (hasDisability === 'notWishToProvide') return '';
  return null;
};
